import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  confirmSignIn,
  signOut as amplifySignOut,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { defaultStorage, sessionStorage } from 'aws-amplify/utils';
import { config } from '../config';

/**
 * Sign-in for the web app, on the same Cognito user pool as the phone, so one
 * EducAitor account works everywhere.
 *
 * "Keep me signed in" decides where tokens live: localStorage survives the
 * browser closing; sessionStorage (the default) forgets them, which is the
 * right choice on a shared school computer.
 */
const REMEMBER_KEY = 'educaitor.remember';

function remembered(): boolean {
  try {
    return localStorage.getItem(REMEMBER_KEY) === '1';
  } catch {
    return false;
  }
}

function useStorage(remember: boolean) {
  try {
    if (remember) localStorage.setItem(REMEMBER_KEY, '1');
    else localStorage.removeItem(REMEMBER_KEY);
  } catch {
    // Private windows can refuse storage; the session still works for this tab.
  }
  cognitoUserPoolsTokenProvider.setKeyValueStorage(remember ? defaultStorage : sessionStorage);
}

export function configureAuth() {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.cognito.userPoolId,
        userPoolClientId: config.cognito.clientId,
        loginWith: {
          email: true,
          ...(config.cognito.domain
            ? {
                oauth: {
                  domain: config.cognito.domain,
                  scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
                  redirectSignIn: [`${window.location.origin}/auth/callback`],
                  redirectSignOut: [`${window.location.origin}/sign-in`],
                  responseType: 'code' as const,
                  providers: ['Google' as const, 'Apple' as const],
                },
              }
            : {}),
        },
      },
    },
  });
  useStorage(remembered());
}

export type SignInStep =
  | { kind: 'done' }
  | { kind: 'code'; message: string }
  | { kind: 'unsupported'; message: string };

function describeStep(step: string): SignInStep {
  switch (step) {
    case 'DONE':
      return { kind: 'done' };
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return { kind: 'code', message: 'Enter the 6-digit code from your authenticator app.' };
    case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
    case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
      return { kind: 'code', message: 'Enter the 6-digit code we just sent you.' };
    case 'CONFIRM_SIGN_UP':
      return { kind: 'unsupported', message: 'Please verify your email in the EducAitor app first, then sign in here.' };
    case 'RESET_PASSWORD':
      return { kind: 'unsupported', message: 'Your password needs resetting. Use "Forgot password?" below.' };
    default:
      return { kind: 'unsupported', message: 'This sign-in step isn’t available on the web yet. Please sign in with the app.' };
  }
}

export async function signIn(email: string, password: string, remember: boolean): Promise<SignInStep> {
  useStorage(remember);
  // A stale session from another account would make Cognito refuse a new sign-in.
  await amplifySignOut().catch(() => {});
  const res = await amplifySignIn({ username: email.trim().toLowerCase(), password });
  return describeStep(res.nextStep.signInStep);
}

export async function submitSignInCode(code: string): Promise<SignInStep> {
  const res = await confirmSignIn({ challengeResponse: code.trim() });
  return describeStep(res.nextStep.signInStep);
}

export async function signOut() {
  await amplifySignOut().catch(() => {});
}

/** A fresh access token, refreshed automatically when it is close to expiry. */
export async function accessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function startPasswordReset(email: string) {
  const res = await resetPassword({ username: email.trim().toLowerCase() });
  return res.nextStep.codeDeliveryDetails?.destination ?? null;
}

export async function finishPasswordReset(email: string, code: string, newPassword: string) {
  await confirmResetPassword({ username: email.trim().toLowerCase(), confirmationCode: code.trim(), newPassword });
}

export type SocialProvider = 'Google' | 'Apple';
const SOCIAL_KEY = 'educaitor.social';

export const socialSignInEnabled = () => Boolean(config.cognito.domain);

/**
 * Off to Google or Apple and back to /auth/callback. Remembers the provider
 * for this tab so the callback can retry once after an account is linked.
 */
export async function signInWithProvider(provider: SocialProvider, opts: { retry?: boolean } = {}) {
  try {
    window.sessionStorage.setItem(SOCIAL_KEY, JSON.stringify({ provider, retried: Boolean(opts.retry) }));
  } catch {
    // Without storage the linked-account retry just won't happen automatically.
  }
  await amplifySignOut().catch(() => {});
  await signInWithRedirect({ provider });
}

export function pendingSocial(): { provider: SocialProvider; retried: boolean } | null {
  try {
    const raw = window.sessionStorage.getItem(SOCIAL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearSocial() {
  try {
    window.sessionStorage.removeItem(SOCIAL_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** Create the sign-in account; Cognito emails a 6-digit code to confirm the address. */
export async function signUp(email: string, password: string, name: string) {
  const res = await amplifySignUp({
    username: email.trim().toLowerCase(),
    password,
    options: { userAttributes: { email: email.trim().toLowerCase(), name: name.trim() } },
  });
  return { needsCode: res.nextStep.signUpStep === 'CONFIRM_SIGN_UP' };
}

export async function confirmSignUp(email: string, code: string) {
  await amplifyConfirmSignUp({ username: email.trim().toLowerCase(), confirmationCode: code.trim() });
}

export async function resendCode(email: string) {
  await resendSignUpCode({ username: email.trim().toLowerCase() });
}

/** Mirrors the user pool's password policy (see mobile/src/utils/passwordPolicy.ts). */
export function passwordChecks(password: string) {
  return [
    { label: '8 or more characters', ok: password.length >= 8 },
    { label: 'upper case letter', ok: /[A-Z]/.test(password) },
    { label: 'lower case letter', ok: /[a-z]/.test(password) },
    { label: 'number', ok: /[0-9]/.test(password) },
    { label: 'symbol', ok: /[^A-Za-z0-9]/.test(password) },
  ];
}

/** Cognito errors in words a teacher would recognise. */
export function authErrorMessage(err: unknown): string {
  const name = (err as { name?: string })?.name ?? '';
  switch (name) {
    case 'NotAuthorizedException':
      return 'That email and password don’t match. Check them and try again.';
    case 'UserNotFoundException':
      return 'We couldn’t find an account with that email.';
    case 'UserNotConfirmedException':
      return 'Please verify your email in the EducAitor app first, then sign in here.';
    case 'CodeMismatchException':
      return 'That code isn’t right. Check it and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Ask for a new one.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'InvalidPasswordException':
      return 'Choose a password of at least 8 characters with upper and lower case letters, a number and a symbol.';
    case 'UsernameExistsException':
      return 'There\u2019s already an account with that email. Sign in instead, or reset your password.';
    case 'InvalidParameterException':
      return 'Check the details you entered and try again.';
    case 'EmptySignInUsername':
    case 'EmptySignInPassword':
      return 'Enter your email and password.';
    default:
      return 'Something went wrong. Check your connection and try again.';
  }
}
